import axios from 'axios';
import { captureNetworkException } from '../../global/error_handler';

// Blob URLs are revoked well after the click so a slow download manager can
// still read the bytes; the page normally reloads or moves on long before this.
const BLOB_URL_REVOKE_MS = 60 * 1000;

/**
 * Hand an already-fetched file to the browser's download manager.
 * Throws (after reporting) when the handoff cannot be started.
 */
const downloadToBrowser = (response, customFileName, fileExt = 'pdf') => {
  try {
    // Explicitly set mime-type so all browsers can download properly
    const downloadBlob = new Blob([response.data], { type: `application/${fileExt}` });

    // For IE, call native method to download file
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(downloadBlob, `${customFileName}.${fileExt}`);
      return;
    }
    // For other browsers, we'll create an invisible link pointing to
    // the URL of the blob, using the download property for <a> tags
    // supported natively in HTML5
    // More here: https://caniuse.com/#feat=download
    const url = window.URL.createObjectURL(downloadBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${customFileName}.${fileExt}`);
    document.body.appendChild(link);
    link.click();
    link.remove?.();
    setTimeout(() => window.URL.revokeObjectURL?.(url), BLOB_URL_REVOKE_MS);
  } catch (e) {
    captureNetworkException(e, { source: 'downloadToBrowser' });
    throw e;
  }
};

/**
 * Fetch the PDF behind a signed URL and save it to the user's computer.
 *
 * Resolves once the bytes are fetched and handed to the browser. Rejects (after
 * reporting) when the fetch or handoff fails, so "Download and Archive" never
 * archives a record the user did not get. A caller that reloads or navigates
 * right afterwards must also await waitForBrowserHandoff() (downloadFlow.js).
 *
 * @param  {String} signedUrl
 * @param  {String} customFileName
 * @return {Promise<Object>} the axios response
 */
const download = async (signedUrl, customFileName = 'file') => {
  let response;
  try {
    response = await axios.get(signedUrl, {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf',
      },
    });
  } catch (error) {
    captureNetworkException(error, { source: 'download', endpoint: signedUrl, method: 'GET' });
    throw error;
  }

  downloadToBrowser(response, customFileName);
  return response;
};

export default download;
export { downloadToBrowser };
